import React from "react";
import { getClassName } from "@utils";
import { Tooltip, TooltipSize } from "../Tooltip";
import css from "./Tabs.module.scss";

export interface TabsProps<T extends string = string> {
  tabs: Array<{
    id: T;
    label: string;
    showIndicator?: boolean;
    tooltip?: string;
  }>;
  activeTabId: T | null;
  onTabClick: (tabId: T) => void;
  disabled?: boolean;
}

const Tabs = <T extends string>({
  tabs,
  activeTabId,
  onTabClick,
  disabled = false,
}: TabsProps<T>): React.JSX.Element => {
  const attrs = {
    tabs: {
      className: getClassName(css.tabs, disabled && css.tabsDisabled),
    },
  };

  const renderTabs = () => {
    return tabs.map(({ id, label, showIndicator = false, tooltip }) => {
      const isActive = activeTabId === id;

      const attrs = {
        tooltip: {
          disabled,
          size: TooltipSize.XS,
          text: tooltip,
          arrow: false,
          autoPosition: true,
          width: 232,
        },
        tab: {
          disabled: isActive,
          type: "button" as const,
          className: getClassName(css.tabItem, showIndicator && css.tabItemWithIndicator),
          onClick: () => {
            onTabClick(id);
          },
        },
      };

      if (tooltip) {
        return (
          <Tooltip key={id} {...attrs.tooltip}>
            <button {...attrs.tab}>
              <span>{label}</span>
            </button>
          </Tooltip>
        );
      }

      return (
        <button key={id} {...attrs.tab}>
          <span>{label}</span>
        </button>
      );
    });
  };

  return <div {...attrs.tabs}>{renderTabs()}</div>;
};

export default Tabs;
